export type PeriodType = "D" | "W" | "M" | "Q" | "Y";
export type PaymentSystemType = "solidgate" | "ecommpay" | "apple" | "truegate";
export interface IPaymentPlan {
    id: string;
    name: string;
    code: string;
    description: string;
    oldPrice: number;
    newPrice: number;
    period: number;
    paymentLink: string;
    periodType: PeriodType;
    currency: string;
    tags: string[];
    trial: {
        TrialPeriod: number;
        TrialPrice: number;
        TrialPeriodType: PeriodType;
    };
}

export interface EcommpayPaymentObject extends Record<string, string> {
    customer_email: string;
    customer_id: string;
    interface_type: string;
    merchant_data: string;
    payment_amount: string;
    payment_currency: string;
    payment_description: string;
    payment_id: string;
    project_id: string;
    recurring: string;
    redirect_fail_url: string;
    redirect_success_url: string;
    signature: string;
}

export interface ISolidgatePrice {
    id: string;
    product_price: number;
    trial_price: number;
    currency: string;
}

export interface ISolidgatePlan {
    id: string;
    name: string;
    description: string;
    public_description: string;
    billing_period: { unit: string; value: number };
    trial?: { billing_period: { unit: string; value: number } } | null;
    prices: ISolidgatePrice[];
    discount?: number;
    // Effective admin-authored plan metadata (plan_metadata, merged for the
    // request locale by the backend). `metadata.name` set means `name` is
    // already the localized admin name. Absent when the plan has none.
    metadata?: Record<string, unknown>;
}

export class SolidgatePlan implements ISolidgatePlan {
    id: string = "";
    name: string = "";
    description: string = "";
    public_description: string = "";
    prices: ISolidgatePrice[] = [];

    billing_period = { unit: "", value: 0 };
    trial: { billing_period: { unit: string; value: number } } | null = null;
    // Declared so the offer keys (discount_percent, prices.<CUR>.old_price /
    // discount_percent — see features/payment/planOffer) are reachable on the
    // restored instance; `restore` copies it with the rest of the response.
    metadata?: Record<string, unknown>;

    get period(): number {
        return this.billing_period.value;
    }

    get periodType(): PeriodType {
        return this.billing_period.unit as PeriodType;
    }

    get discount(): number {
        if (!this?.discount) return 0;

        return this.discount;
    }

    restore(p: ISolidgatePlan): SolidgatePlan {
        Object.assign(this, p);
        return this;
    }
}

export type SolidFormResponse = {
    merchant: string;
    partilIntent: string;
    signature: string;
    order_id?: string;
    // A BRL subscription intent carrying the Pix Automático mandate: its product
    // is on a billing period the Pix schedule accepts.
    pixAutomatico?: boolean;
};

export type SolidUpdateCardFormResponse = {
    merchant: string;
    partilIntent: string;
    signature: string;
    order_id: string;
};

export type UpdateCardStatusResponse = {
    status: "pending" | "success" | "failed";
    message?: string;
};

export interface CountryResponse {
    country: string;
}

export interface ITruegatePlanPrice {
    id: string;
    product_price: number;
    trial_price: number;
    status: string;
    currency: string;
}

export interface ITruegatePlan {
    id: number;
    plan_code: string;
    truegate_id: string;
    name: string;
    description: string;
    type: string;
    status: string;
    billing_period: { unit: string; value: number };
    trial: { billing_period: { unit: string; value: number } } | null;
    prices: ITruegatePlanPrice[];
    tags: string[];
    /** Effective admin-authored plan metadata (plan_metadata); absent when none. */
    metadata?: Record<string, unknown>;
}

export interface TruegateChargeResponse {
    transactionId: string;
    status: string;
    amount: string;
    currency: string;
    externalUserId: string;
}
